import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth, API, getActiveAccountHeader } from '../App';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { jsPDF } from 'jspdf';
import { 
  FileText, Plus, Download, Trash2, ClipboardList, 
  Search, Calendar, DollarSign, Package, ChevronRight
} from 'lucide-react';

// Tack type labels
const TACK_TYPES = [
  { value: 'saddle', label: 'Saddle' },
  { value: 'bridle', label: 'Bridle' },
  { value: 'cover', label: 'Cover' },
  { value: 'girth', label: 'Girth' },
  { value: 'saddle_pad', label: 'Saddle Pad' },
  { value: 'horse_boots', label: 'Horse Boots' },
  { value: 'halter_lead', label: 'Halter/Lead' },
  { value: 'transport', label: 'Transport' },
  { value: 'rider_apparel', label: 'Rider Apparel' },
  { value: 'miscellaneous', label: 'Miscellaneous' }
];

export const ReportsPage = () => {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('type') || 'saved');
  const [loading, setLoading] = useState(true);
  
  // Data
  const [savedReports, setSavedReports] = useState([]);
  const [horses, setHorses] = useState([]);
  const [gear, setGear] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [riders, setRiders] = useState([]);
  
  // Horse report state
  const [selectedHorse, setSelectedHorse] = useState('');
  const [horseReportOptions, setHorseReportOptions] = useState({ feed: false, health: false, events: false, rides: false });
  const [horseDateRange, setHorseDateRange] = useState('all');
  const [horseDateFrom, setHorseDateFrom] = useState('');
  const [horseDateTo, setHorseDateTo] = useState('');
  
  // Tack report state
  const [tackFilter, setTackFilter] = useState('all');
  const [selectedTack, setSelectedTack] = useState([]);
  const [selectAllTack, setSelectAllTack] = useState(false);
  
  // Expense report state
  const [expenseFilter, setExpenseFilter] = useState('all');
  const [expenseDateFrom, setExpenseDateFrom] = useState('');
  const [expenseDateTo, setExpenseDateTo] = useState('');
  
  // Dialogs
  const [horseDialogOpen, setHorseDialogOpen] = useState(false);
  const [tackDialogOpen, setTackDialogOpen] = useState(searchParams.get('type') === 'tack');
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(searchParams.get('type') === 'expense');
  const [generating, setGenerating] = useState(false);

  // Get headers dynamically to ensure fresh activeAccountId after account switch
  const getHeaders = () => {
    const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
    const h = { 'Content-Type': 'application/json' };
    if (activeAccountId) h['X-Active-Account'] = activeAccountId;
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  useEffect(() => {
    if (searchParams.get('type') === 'tack') {
      setTackDialogOpen(true);
    } else if (searchParams.get('type') === 'expense') {
      setExpenseDialogOpen(true);
    }
  }, [searchParams]);

  const fetchData = async () => {
    const headers = getHeaders();
    try {
      const [reportsRes, horsesRes, gearRes, expensesRes, ridersRes] = await Promise.all([
        fetch(`${API}/reports`, { headers }),
        fetch(`${API}/horses`, { headers }),
        fetch(`${API}/gear`, { headers }),
        fetch(`${API}/expenses`, { headers }),
        fetch(`${API}/rider-profiles`, { headers })
      ]);

      if (reportsRes.ok) setSavedReports(await reportsRes.json());
      if (horsesRes.ok) setHorses(await horsesRes.json());
      if (gearRes.ok) setGear(await gearRes.json());
      if (expensesRes.ok) setExpenses(await expensesRes.json());
      if (ridersRes.ok) setRiders(await ridersRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const getHorseName = (horseId) => horses.find(h => h.horse_id === horseId)?.stable_name || 'Unknown';
  const getRiderName = (riderId) => riders.find(r => r.rider_id === riderId)?.name || 'Unknown';
  const getTackTypeName = (type) => TACK_TYPES.find(t => t.value === type)?.label || type || 'Unknown';

  // Filter tack based on current filter
  const filteredTack = gear.filter(item => {
    if (tackFilter === 'all') return true;
    if (tackFilter === 'general') return item.allocation_type === 'general' || (!item.horse_id && !item.rider_id);
    if (tackFilter === 'horses') return item.allocation_type === 'horse' && item.horse_id;
    if (tackFilter === 'riders') return item.allocation_type === 'rider' && item.rider_id;
    if (tackFilter.startsWith('type_')) return item.tack_type === tackFilter.replace('type_', '');
    if (tackFilter.startsWith('horse_')) return item.horse_id === tackFilter.replace('horse_', '');
    if (tackFilter.startsWith('rider_')) return item.rider_id === tackFilter.replace('rider_', '');
    return true;
  });

  // Filter expenses
  const filteredExpenses = expenses.filter(expense => {
    let matchesFilter = true;
    if (expenseFilter === 'general') matchesFilter = !expense.horse_id;
    else if (expenseFilter === 'horses_only') matchesFilter = expense.horse_id;
    else if (expenseFilter !== 'all') matchesFilter = expense.horse_id === expenseFilter;
    
    let matchesDate = true;
    if (expenseDateFrom && expense.date < expenseDateFrom) matchesDate = false;
    if (expenseDateTo && expense.date > expenseDateTo) matchesDate = false;
    
    return matchesFilter && matchesDate;
  });

  // Handle select all tack
  useEffect(() => {
    if (selectAllTack) {
      setSelectedTack(filteredTack.map(t => t.gear_id));
    } else if (selectedTack.length === filteredTack.length && filteredTack.length > 0) {
      // Don't clear if user manually selected all
    }
  }, [selectAllTack, filteredTack.length]);

  const toggleTackSelection = (gearId) => {
    setSelectedTack(prev => 
      prev.includes(gearId) 
        ? prev.filter(id => id !== gearId)
        : [...prev, gearId]
    );
  };

  // Generate Horse Report PDF
  const generateHorseReport = async () => {
    if (!selectedHorse) {
      toast.error('Please select a horse');
      return;
    }
    if (!horseReportOptions.feed && !horseReportOptions.health && !horseReportOptions.events && !horseReportOptions.rides) {
      toast.error('Please select at least one information type');
      return;
    }

    setGenerating(true);
    try {
      const headers = getHeaders();
      const horse = horses.find(h => h.horse_id === selectedHorse);
      if (!horse) throw new Error('Horse not found');

      // Helper to filter by date range
      const filterByDateRange = (items, dateField = 'date') => {
        if (horseDateRange === 'all' || (!horseDateFrom && !horseDateTo)) return items;
        return items.filter(item => {
          const itemDate = item[dateField];
          if (!itemDate) return true; // Include items without dates
          if (horseDateFrom && itemDate < horseDateFrom) return false;
          if (horseDateTo && itemDate > horseDateTo) return false;
          return true;
        });
      };

      // Fetch additional data
      const fetchPromises = [];
      if (horseReportOptions.feed) fetchPromises.push(fetch(`${API}/feed-entries?horse_id=${selectedHorse}`, {  headers }));
      if (horseReportOptions.health) fetchPromises.push(fetch(`${API}/health-records?horse_id=${selectedHorse}`, {  headers }));
      if (horseReportOptions.events) fetchPromises.push(fetch(`${API}/event-logs?horse_id=${selectedHorse}`, {  headers }));
      if (horseReportOptions.rides) fetchPromises.push(fetch(`${API}/ride-logs?horse_id=${selectedHorse}`, {  headers }));

      const responses = await Promise.all(fetchPromises);
      let feedData = [], healthData = [], eventData = [], rideData = [];
      let idx = 0;
      if (horseReportOptions.feed && responses[idx]?.ok) feedData = await responses[idx++].json();
      if (horseReportOptions.health && responses[idx]?.ok) healthData = filterByDateRange(await responses[idx++].json());
      if (horseReportOptions.events && responses[idx]?.ok) eventData = filterByDateRange(await responses[idx++].json());
      if (horseReportOptions.rides && responses[idx]?.ok) rideData = filterByDateRange(await responses[idx].json());

      // Generate PDF
      const doc = new jsPDF();
      let y = 20;

      // Header
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Horse Report', 105, y, { align: 'center' });
      y += 10;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated: ${format(new Date(), 'MMM d, yyyy HH:mm')}`, 105, y, { align: 'center' });
      y += 5;
      if (horseDateRange === 'custom' && (horseDateFrom || horseDateTo)) {
        const dateRangeText = `Date Range: ${horseDateFrom || 'Start'} to ${horseDateTo || 'Present'}`;
        doc.text(dateRangeText, 105, y, { align: 'center' });
        y += 5;
      }
      y += 10;

      // Horse ID Profile
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Horse Profile', 14, y);
      y += 8;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const profileLines = [
        `Registered Name: ${horse.registered_name}`,
        `Stable Name: ${horse.stable_name}`,
        `Breed: ${horse.breed || 'N/A'}`,
        `Colour: ${horse.colour || 'N/A'}`,
        `Sex: ${horse.sex || 'N/A'}`,
        `DOB: ${horse.dob || 'N/A'}`,
        `Height: ${horse.height || 'N/A'}`,
        `Microchip: ${horse.microchip || 'N/A'}`,
        `Registration: ${horse.registration_number || 'N/A'} (${horse.registration_society || 'N/A'})`,
        `Sire: ${horse.sire || 'N/A'}`,
        `Dam: ${horse.dam || 'N/A'}`,
        `Owner: ${horse.owner || 'N/A'}`
      ];
      profileLines.forEach(line => {
        doc.text(line, 14, y);
        y += 5;
      });
      y += 10;

      // Feed Section
      if (horseReportOptions.feed && feedData.length > 0) {
        if (y > 250) { doc.addPage(); y = 20; }
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Feed Schedule', 14, y);
        y += 8;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        
        const latestFeed = feedData[0];
        if (latestFeed?.items) {
          latestFeed.items.forEach(item => {
            doc.text(`• ${item.item_name}: ${item.amount} (${item.frequency?.replace('_', ' ')})`, 18, y);
            y += 5;
          });
        }
        y += 10;
      }

      // Health Section
      if (horseReportOptions.health && healthData.length > 0) {
        if (y > 200) { doc.addPage(); y = 20; }
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Health Records', 14, y);
        y += 8;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        
        healthData.slice(0, 20).forEach(record => {
          if (y > 270) { doc.addPage(); y = 20; }
          const recordLine = `${record.date || 'N/A'} - ${record.record_type}: ${record.provider || 'N/A'}${record.next_due_date ? ` (Next: ${record.next_due_date})` : ''}`;
          doc.text(recordLine, 18, y);
          y += 5;
          
          // Insemination details
          if (record.record_type === 'insemination') {
            doc.setFontSize(9);
            doc.setTextColor(100, 100, 100);
            const insemDetails = [];
            if (record.sire) insemDetails.push(`Sire: ${record.sire}`);
            if (record.service_type) {
              const serviceTypeDisplay = record.service_type === 'natural' ? 'Natural' : 
                record.service_type === 'ai_fresh' ? 'AI Fresh' : 
                record.service_type === 'ai_frozen' ? 'AI Frozen' : record.service_type;
              insemDetails.push(`Service: ${serviceTypeDisplay}`);
            }
            if (insemDetails.length > 0) {
              doc.text(`   ${insemDetails.join(' | ')}`, 18, y);
              y += 5;
            }
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(10);
          }
          
          // Foaling details
          if (record.record_type === 'foaling') {
            doc.setFontSize(9);
            doc.setTextColor(100, 100, 100);
            const foalingDetails = [];
            if (record.sire) foalingDetails.push(`Sire: ${record.sire}`);
            if (record.outcome) {
              const outcomeDisplay = record.outcome === 'live_foal' ? 'Live Foal' : 'Stillbirth';
              foalingDetails.push(`Outcome: ${outcomeDisplay}`);
            }
            if (record.foal_sex) {
              const sexDisplay = record.foal_sex === 'colt' ? 'Colt' : 'Filly';
              foalingDetails.push(`Sex: ${sexDisplay}`);
            }
            if (foalingDetails.length > 0) {
              doc.text(`   ${foalingDetails.join(' | ')}`, 18, y);
              y += 5;
            }
            const foalingDetails2 = [];
            if (record.number_of_foals) {
              const countDisplay = record.number_of_foals === 'single' ? 'Single' : 'Twin';
              foalingDetails2.push(`Foals: ${countDisplay}`);
            }
            if (record.delivery_type) {
              const deliveryDisplay = record.delivery_type === 'normal' ? 'Normal Foaling' : 'Assisted Delivery';
              foalingDetails2.push(`Delivery: ${deliveryDisplay}`);
            }
            if (foalingDetails2.length > 0) {
              doc.text(`   ${foalingDetails2.join(' | ')}`, 18, y);
              y += 5;
            }
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(10);
          }
          
          // Notes for all record types
          if (record.notes) {
            const truncatedNotes = record.notes.length > 80 ? record.notes.substring(0, 80) + '...' : record.notes;
            doc.setFontSize(9);
            doc.setTextColor(100, 100, 100);
            doc.text(`   ${truncatedNotes}`, 18, y);
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(10);
            y += 5;
          }
        });
        y += 10;
      }

      // Events Section
      if (horseReportOptions.events && eventData.length > 0) {
        if (y > 200) { doc.addPage(); y = 20; }
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Events', 14, y);
        y += 8;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        
        eventData.slice(0, 20).forEach(event => {
          if (y > 270) { doc.addPage(); y = 20; }
          const eventLine = `${event.date} - ${event.event_name}${event.placing ? ` (${event.placing})` : ''}${event.location ? ` @ ${event.location}` : ''}`;
          doc.text(eventLine, 18, y);
          y += 5;
        });
      }

      // Ride Logs Section
      if (horseReportOptions.rides && rideData.length > 0) {
        if (y > 200) { doc.addPage(); y = 20; }
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Ride Logs', 14, y);
        y += 8;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        
        rideData.slice(0, 20).forEach(ride => {
          if (y > 270) { doc.addPage(); y = 20; }
          const duration = ride.duration ? `${ride.duration} min` : '';
          const rider = ride.rider_name ? `Rider: ${ride.rider_name}` : '';
          const rideLine = `${ride.date} - ${ride.activity_type || 'Ride'}${duration ? ` (${duration})` : ''}${rider ? ` - ${rider}` : ''}`;
          doc.text(rideLine, 18, y);
          y += 5;
          if (ride.notes) {
            const truncatedNotes = ride.notes.length > 80 ? ride.notes.substring(0, 80) + '...' : ride.notes;
            doc.setFontSize(9);
            doc.text(`  Notes: ${truncatedNotes}`, 18, y);
            doc.setFontSize(10);
            y += 5;
          }
        });
      }

      // Save PDF
      const pdfBlob = doc.output('blob');
      const filename = `${horse.stable_name.replace(/\s+/g, '_')}-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      
      // Save to backend
      const formData = new FormData();
      formData.append('file', pdfBlob, filename);
      formData.append('report_type', 'horse');
      formData.append('title', `${horse.stable_name} Report`);
      
      const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
      const uploadHeaders = {};
      if (token) uploadHeaders['Authorization'] = `Bearer ${token}`;
      if (activeAccountId) uploadHeaders['X-Active-Account'] = activeAccountId;
      
      const saveRes = await fetch(`${API}/reports`, {
        method: 'POST',
        headers: uploadHeaders,
        body: formData
      });

      if (saveRes.ok) {
        toast.success('Report generated and saved!');
        fetchData();
        setHorseDialogOpen(false);
        // Download
        doc.save(filename);
      } else {
        throw new Error('Failed to save report');
      }
    } catch (error) {
      console.error('Failed to generate report:', error);
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  // Generate Tack Report PDF
  const generateTackReport = async () => {
    if (selectedTack.length === 0) {
      toast.error('Please select at least one tack item');
      return;
    }

    setGenerating(true);
    try {
      const selectedItems = gear.filter(g => selectedTack.includes(g.gear_id));
      
      // Generate PDF
      const doc = new jsPDF();
      let y = 20;

      // Header with account holder
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Tack Inventory Report', 105, y, { align: 'center' });
      y += 10;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated: ${format(new Date(), 'MMM d, yyyy HH:mm')}`, 105, y, { align: 'center' });
      y += 8;
      
      // Account holder details
      const ownerName = user?.use_business_as_owner && user?.business_name ? user.business_name : user?.name || 'N/A';
      doc.text(`Owner: ${ownerName}`, 105, y, { align: 'center' });
      if (user?.email) {
        y += 5;
        doc.text(`Email: ${user.email}`, 105, y, { align: 'center' });
      }
      if (user?.phone_number) {
        y += 5;
        doc.text(`Phone: ${user.phone_number}`, 105, y, { align: 'center' });
      }
      y += 15;

      // Line separator
      doc.setDrawColor(200);
      doc.line(14, y, 196, y);
      y += 10;

      // Tack items
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`Tack Items (${selectedItems.length})`, 14, y);
      y += 10;

      const totalValue = selectedItems.reduce((sum, item) => sum + (item.cost || 0), 0);

      selectedItems.forEach((item, idx) => {
        if (y > 250) { doc.addPage(); y = 20; }
        
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(`${idx + 1}. ${item.item_name}`, 14, y);
        y += 6;
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        
        if (item.tack_type) {
          doc.text(`Type: ${getTackTypeName(item.tack_type)}`, 18, y);
          y += 4;
        }
        if (item.size) {
          doc.text(`Size: ${item.size}`, 18, y);
          y += 4;
        }
        if (item.serial_number) {
          doc.text(`Serial: ${item.serial_number}`, 18, y);
          y += 4;
        }
        if (item.cost) {
          doc.text(`Cost: $${item.cost.toFixed(2)}`, 18, y);
          y += 4;
        }
        if (item.purchase_date) {
          doc.text(`Purchased: ${item.purchase_date}`, 18, y);
          y += 4;
        }
        if (item.purchase_location) {
          doc.text(`Location: ${item.purchase_location}`, 18, y);
          y += 4;
        }
        y += 4;
      });

      // Total value
      y += 5;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`Total Value: $${totalValue.toFixed(2)}`, 14, y);

      // Save PDF
      const pdfBlob = doc.output('blob');
      const filename = `Tack_Report-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      
      const formData = new FormData();
      formData.append('file', pdfBlob, filename);
      formData.append('report_type', 'tack');
      formData.append('title', `Tack Report (${selectedItems.length} items)`);
      
      const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
      const uploadHeaders = {};
      if (token) uploadHeaders['Authorization'] = `Bearer ${token}`;
      if (activeAccountId) uploadHeaders['X-Active-Account'] = activeAccountId;
      
      const saveRes = await fetch(`${API}/reports`, {
        method: 'POST',
        headers: uploadHeaders,
        body: formData
      });

      if (saveRes.ok) {
        toast.success('Report generated and saved!');
        fetchData();
        setTackDialogOpen(false);
        setSelectedTack([]);
        doc.save(filename);
      } else {
        throw new Error('Failed to save report');
      }
    } catch (error) {
      console.error('Failed to generate report:', error);
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  // Generate Expense Report PDF
  const generateExpenseReport = async () => {
    if (filteredExpenses.length === 0) {
      toast.error('No expenses match the current filter');
      return;
    }

    setGenerating(true);
    try {
      const doc = new jsPDF();
      let y = 20;

      // Header
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Expense Report', 105, y, { align: 'center' });
      y += 10;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated: ${format(new Date(), 'MMM d, yyyy HH:mm')}`, 105, y, { align: 'center' });
      y += 8;
      
      // Account holder details
      const ownerName = user?.use_business_as_owner && user?.business_name ? user.business_name : user?.name || 'N/A';
      doc.text(`Owner: ${ownerName}`, 105, y, { align: 'center' });
      if (user?.email) {
        y += 5;
        doc.text(`Email: ${user.email}`, 105, y, { align: 'center' });
      }
      y += 15;

      // Date range if set
      if (expenseDateFrom || expenseDateTo) {
        doc.text(`Period: ${expenseDateFrom || 'Start'} to ${expenseDateTo || 'Present'}`, 105, y, { align: 'center' });
        y += 8;
      }

      doc.setDrawColor(200);
      doc.line(14, y, 196, y);
      y += 10;

      // Expenses
      const totalAmount = filteredExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`Expenses (${filteredExpenses.length}) - Total: $${totalAmount.toFixed(2)}`, 14, y);
      y += 10;

      // Group by category
      const byCategory = {};
      filteredExpenses.forEach(exp => {
        const cat = exp.category || 'Other';
        if (!byCategory[cat]) byCategory[cat] = [];
        byCategory[cat].push(exp);
      });

      Object.entries(byCategory).forEach(([category, catExpenses]) => {
        if (y > 250) { doc.addPage(); y = 20; }
        
        const catTotal = catExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(`${category.charAt(0).toUpperCase() + category.slice(1)} - $${catTotal.toFixed(2)}`, 14, y);
        y += 6;
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        
        catExpenses.forEach(exp => {
          if (y > 270) { doc.addPage(); y = 20; }
          const horseName = exp.horse_id ? getHorseName(exp.horse_id) : 'General';
          const line = `${exp.date} - $${exp.amount?.toFixed(2) || '0.00'} - ${horseName}${exp.vendor ? ` @ ${exp.vendor}` : ''}`;
          doc.text(line, 18, y);
          y += 4;
          if (exp.notes) {
            const truncatedNotes = exp.notes.length > 70 ? exp.notes.substring(0, 70) + '...' : exp.notes;
            doc.setTextColor(100, 100, 100);
            doc.text(`   ${truncatedNotes}`, 18, y);
            doc.setTextColor(0, 0, 0);
            y += 4;
          }
        });
        y += 4;
      });

      // Save PDF
      const pdfBlob = doc.output('blob');
      const filename = `Expense_Report-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      
      const formData = new FormData();
      formData.append('file', pdfBlob, filename);
      formData.append('report_type', 'expense');
      formData.append('title', `Expense Report ($${totalAmount.toFixed(2)})`);
      
      const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
      const uploadHeaders = {};
      if (token) uploadHeaders['Authorization'] = `Bearer ${token}`;
      if (activeAccountId) uploadHeaders['X-Active-Account'] = activeAccountId;
      
      const saveRes = await fetch(`${API}/reports`, {
        method: 'POST',
        headers: uploadHeaders,
        body: formData
      });

      if (saveRes.ok) {
        toast.success('Report generated and saved!');
        fetchData();
        setExpenseDialogOpen(false);
        doc.save(filename);
      } else {
        throw new Error('Failed to save report');
      }
    } catch (error) {
      console.error('Failed to generate report:', error);
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  // Download saved report
  const downloadReport = async (report) => {
    try {
      const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
      const downloadHeaders = {};
      if (token) downloadHeaders['Authorization'] = `Bearer ${token}`;
      if (activeAccountId) downloadHeaders['X-Active-Account'] = activeAccountId;
      
      const response = await fetch(`${API}/reports/${report.report_id}/download`, {
        headers: downloadHeaders
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = report.filename || `report_${report.report_id}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Report downloaded');
      } else {
        throw new Error('Download failed');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download report');
    }
  };

  // Delete report
  const deleteReport = async (reportId) => {
    try {
      const response = await fetch(`${API}/reports/${reportId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      
      if (response.ok) {
        setSavedReports(prev => prev.filter(r => r.report_id !== reportId));
        toast.success('Report deleted');
      }
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 space-y-6">
          <div className="h-10 w-32 skeleton rounded-lg" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 skeleton rounded-xl" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="reports-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-serif text-foreground">Report Vault</h1>
            <p className="text-muted-foreground mt-1">
              Generate and manage your reports
            </p>
          </div>
        </div>

        {/* Generate Report Options */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Horse Report */}
          <Dialog open={horseDialogOpen} onOpenChange={setHorseDialogOpen}>
            <DialogTrigger asChild>
              <Card className="border-0 shadow-card bg-white cursor-pointer card-hover" data-testid="generate-horse-report-card">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 bg-forest/10 rounded-full flex items-center justify-center">
                    <FileText className="w-6 h-6 text-forest" />
                  </div>
                  <h3 className="font-semibold">Horse Report</h3>
                  <p className="text-sm text-muted-foreground mt-1">Generate profile with feed, health & events</p>
                </CardContent>
              </Card>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-serif">Generate Horse Report</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Select Horse *</Label>
                  <Select value={selectedHorse} onValueChange={setSelectedHorse}>
                    <SelectTrigger data-testid="horse-report-select">
                      <SelectValue placeholder="Choose a horse" />
                    </SelectTrigger>
                    <SelectContent>
                      {horses.filter(h => h.status === 'current').map(horse => (
                        <SelectItem key={horse.horse_id} value={horse.horse_id}>{horse.stable_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Information to Include</Label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="feed" 
                        checked={horseReportOptions.feed}
                        onCheckedChange={(checked) => setHorseReportOptions(prev => ({ ...prev, feed: checked }))}
                        data-testid="horse-report-feed-checkbox"
                      />
                      <label htmlFor="feed" className="text-sm cursor-pointer">Feed Schedule</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="health" 
                        checked={horseReportOptions.health}
                        onCheckedChange={(checked) => setHorseReportOptions(prev => ({ ...prev, health: checked }))}
                        data-testid="horse-report-health-checkbox"
                      />
                      <label htmlFor="health" className="text-sm cursor-pointer">Health Records</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="events" 
                        checked={horseReportOptions.events}
                        onCheckedChange={(checked) => setHorseReportOptions(prev => ({ ...prev, events: checked }))}
                        data-testid="horse-report-events-checkbox"
                      />
                      <label htmlFor="events" className="text-sm cursor-pointer">Events</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="rides" 
                        checked={horseReportOptions.rides}
                        onCheckedChange={(checked) => setHorseReportOptions(prev => ({ ...prev, rides: checked }))}
                        data-testid="horse-report-rides-checkbox"
                      />
                      <label htmlFor="rides" className="text-sm cursor-pointer">Ride Logs</label>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Date Range</Label>
                  <Select value={horseDateRange} onValueChange={(v) => { setHorseDateRange(v); if (v === 'all') { setHorseDateFrom(''); setHorseDateTo(''); } }}>
                    <SelectTrigger data-testid="horse-report-date-range-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Time</SelectItem>
                      <SelectItem value="custom">Custom Date Range</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {horseDateRange === 'custom' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>From Date</Label>
                      <Input 
                        type="date" 
                        value={horseDateFrom} 
                        onChange={(e) => setHorseDateFrom(e.target.value)}
                        data-testid="horse-report-date-from"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>To Date</Label>
                      <Input 
                        type="date" 
                        value={horseDateTo} 
                        onChange={(e) => setHorseDateTo(e.target.value)}
                        data-testid="horse-report-date-to"
                      />
                    </div>
                  </div>
                )}

                <Button 
                  onClick={generateHorseReport} 
                  className="w-full bg-forest hover:bg-forest-dark"
                  disabled={generating}
                  data-testid="generate-horse-report-btn"
                >
                  {generating ? 'Generating...' : 'Generate Report'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Tack Report */}
          <Dialog open={tackDialogOpen} onOpenChange={setTackDialogOpen}>
            <DialogTrigger asChild>
              <Card className="border-0 shadow-card bg-white cursor-pointer card-hover" data-testid="generate-tack-report-card">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 bg-saddle/10 rounded-full flex items-center justify-center">
                    <Package className="w-6 h-6 text-saddle" />
                  </div>
                  <h3 className="font-semibold">Tack Report</h3>
                  <p className="text-sm text-muted-foreground mt-1">Generate inventory list with details</p>
                </CardContent>
              </Card>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif">Generate Tack Report</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="flex gap-4 items-end">
                  <div className="flex-1 space-y-2">
                    <Label>Filter By</Label>
                    <Select value={tackFilter} onValueChange={(v) => { setTackFilter(v); setSelectedTack([]); setSelectAllTack(false); }}>
                      <SelectTrigger data-testid="tack-report-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Items</SelectItem>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="horses">All Horses</SelectItem>
                        <SelectItem value="riders">All Riders</SelectItem>
                        {TACK_TYPES.map(type => (
                          <SelectItem key={`type_${type.value}`} value={`type_${type.value}`}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center space-x-2 pb-2">
                    <Checkbox 
                      id="selectAll" 
                      checked={selectAllTack}
                      onCheckedChange={setSelectAllTack}
                      data-testid="tack-report-select-all"
                    />
                    <label htmlFor="selectAll" className="text-sm cursor-pointer">Select All</label>
                  </div>
                </div>

                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  {filteredTack.length === 0 ? (
                    <p className="p-4 text-center text-muted-foreground">No tack items match filter</p>
                  ) : (
                    filteredTack.map(item => (
                      <div 
                        key={item.gear_id} 
                        className="flex items-center gap-3 p-3 border-b last:border-0 hover:bg-muted/30 cursor-pointer"
                        onClick={() => toggleTackSelection(item.gear_id)}
                        data-testid={`tack-item-${item.gear_id}`}
                      >
                        <Checkbox 
                          checked={selectedTack.includes(item.gear_id)}
                          onCheckedChange={(e) => e.stopPropagation?.()}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex-1">
                          <p className="font-medium">{item.item_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {getTackTypeName(item.tack_type)} {item.size && `• ${item.size}`} {item.cost && `• $${item.cost.toFixed(2)}`}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <p className="text-sm text-muted-foreground">{selectedTack.length} item(s) selected</p>

                <Button 
                  onClick={generateTackReport} 
                  className="w-full bg-forest hover:bg-forest-dark"
                  disabled={generating || selectedTack.length === 0}
                  data-testid="generate-tack-report-btn"
                >
                  {generating ? 'Generating...' : 'Generate Report'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Expense Report */}
          <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
            <DialogTrigger asChild>
              <Card className="border-0 shadow-card bg-white cursor-pointer card-hover" data-testid="generate-expense-report-card">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 bg-amber-100 rounded-full flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="font-semibold">Expense Report</h3>
                  <p className="text-sm text-muted-foreground mt-1">Generate expense summary by filters</p>
                </CardContent>
              </Card>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-serif">Generate Expense Report</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Filter By Horse</Label>
                  <Select value={expenseFilter} onValueChange={setExpenseFilter}>
                    <SelectTrigger data-testid="expense-report-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All (Horses + General)</SelectItem>
                      <SelectItem value="horses_only">All Horses</SelectItem>
                      <SelectItem value="general">General Only</SelectItem>
                      {horses.filter(h => h.status === 'current').map(horse => (
                        <SelectItem key={horse.horse_id} value={horse.horse_id}>{horse.stable_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>From Date</Label>
                    <Input 
                      type="date" 
                      value={expenseDateFrom} 
                      onChange={(e) => setExpenseDateFrom(e.target.value)}
                      data-testid="expense-report-date-from"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>To Date</Label>
                    <Input 
                      type="date" 
                      value={expenseDateTo} 
                      onChange={(e) => setExpenseDateTo(e.target.value)}
                      data-testid="expense-report-date-to"
                    />
                  </div>
                </div>

                <p className="text-sm text-muted-foreground">
                  {filteredExpenses.length} expense(s) • Total: ${filteredExpenses.reduce((sum, e) => sum + (e.amount || 0), 0).toFixed(2)}
                </p>

                <Button 
                  onClick={generateExpenseReport} 
                  className="w-full bg-forest hover:bg-forest-dark"
                  disabled={generating || filteredExpenses.length === 0}
                  data-testid="generate-expense-report-btn"
                >
                  {generating ? 'Generating...' : 'Generate Report'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Saved Reports */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold font-serif">Saved Reports</h2>
          
          {savedReports.length === 0 ? (
            <Card className="border-0 shadow-card bg-white">
              <CardContent className="py-12 text-center">
                <ClipboardList className="w-12 h-12 mx-auto mb-4 text-muted-foreground/20" />
                <h3 className="font-semibold mb-2">No Saved Reports</h3>
                <p className="text-muted-foreground text-sm">Generate a report above to save it here</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {savedReports.map(report => (
                <Card key={report.report_id} className="border-0 shadow-card bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-forest" />
                          <span className="font-medium">{report.title}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {report.report_type} • {report.created_at ? format(parseISO(report.created_at), 'MMM d, yyyy HH:mm') : 'N/A'}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={() => downloadReport(report)}
                          data-testid={`download-report-${report.report_id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-destructive"
                              data-testid={`delete-report-${report.report_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Report?</AlertDialogTitle>
                              <AlertDialogDescription>This will permanently delete this saved report.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteReport(report.report_id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};
